import {TCurrency} from '@edna/utils';

import {TItemId} from '../../constants';

enum EPermission {
  // User permissions
  PERMISSION_READ = 'PERMISSION_READ',
  PERMISSION_WRITE = 'PERMISSION_WRITE',
  PERMISSION_BROADCAST_WRITE = 'PERMISSION_BROADCAST_WRITE',
  PERMISSION_BROADCAST_READ = 'PERMISSION_BROADCAST_READ',
  PERMISSION_BALANCE_READ = 'PERMISSION_BALANCE_READ',
  PERMISSION_TRANSACTION_READ = 'PERMISSION_TRANSACTION_READ',
  PERMISSION_VIEW_BILLING = 'PERMISSION_VIEW_BILLING',
  PERMISSION_BROADCAST_FLOW_INTEGRATION = 'PERMISSION_BROADCAST_FLOW_INTEGRATION',
  PERMISSION_LICENSE_BLOCK = 'PERMISSION_LICENSE_BLOCK',
  PERMISSION_TENANT_LICENSE_BLOCK = 'PERMISSION_TENANT_LICENSE_BLOCK',
  PERMISSION_FLOW = 'PERMISSION_FLOW',
  PERMISSION_TENANT_FLOW_READ = 'PERMISSION_TENANT_FLOW_READ',
  PERMISSION_STRIPE_ONETIME_INVOICE = 'PERMISSION_STRIPE_ONETIME_INVOICE',
  PERMISSION_VIEW_PAYKEEPER = 'PERMISSION_VIEW_PAYKEEPER',
  PERMISSION_PAYKEEPER_CHANGE = 'PERMISSION_PAYKEEPER_CHANGE',

  // Channels
  PERMISSION_WHATSAPP_PROFILE = 'PERMISSION_WHATSAPP_PROFILE',
  PERMISSION_VK_NOTIFY = 'PERMISSION_VK_NOTIFY',
  PERMISSION_OK_NOTIFY = 'PERMISSION_OK_NOTIFY',
  PERMISSION_MAX_BOT = 'PERMISSION_MAX_BOT',
  PERMISSION_MAX = 'PERMISSION_MAX',
  PERMISSION_TG = 'PERMISSION_TG',

  //  Support permissions
  PERMISSION_TENANT_LIST_READ = 'PERMISSION_TENANT_LIST_READ',
  PERMISSION_TENANT_DATA_READ = 'PERMISSION_TENANT_DATA_READ',
  PERMISSION_TENANT_LICENSE_READ = 'PERMISSION_TENANT_LICENSE_READ',
  PERMISSION_TENANT_CALLBACK_READ = 'PERMISSION_TENANT_CALLBACK_READ',
  PERMISSION_TENANT_CALLBACK_WRITE = 'PERMISSION_TENANT_CALLBACK_WRITE',
  PERMISSION_TENANT_APIKEY_READ = 'PERMISSION_TENANT_APIKEY_READ',
  PERMISSION_TENANT_APIKEY_WRITE = 'PERMISSION_TENANT_APIKEY_WRITE',
  PERMISSION_TENANT_DETAILS_READ = 'PERMISSION_TENANT_DETAILS_READ',
  PERMISSION_TENANT_DETAILS_WRITE = 'PERMISSION_TENANT_DETAILS_WRITE',
  PERMISSION_TARIFF_TEMPLATE_READ = 'PERMISSION_TARIFF_TEMPLATE_READ',
  PERMISSION_TARIFF_TEMPLATE_WRITE = 'PERMISSION_TARIFF_TEMPLATE_WRITE',
  PERMISSION_TENANT_TARIFF_READ = 'PERMISSION_TENANT_TARIFF_READ',
  PERMISSION_TENANT_TARIFF_WRITE = 'PERMISSION_TENANT_TARIFF_WRITE',
  PERMISSION_TENANT_TRANSACTION_READ = 'PERMISSION_TENANT_TRANSACTION_READ',
  PERMISSION_TENANT_BALANCE_READ = 'PERMISSION_TENANT_BALANCE_READ',
  PERMISSION_TENANT_BALANCE_ADD = 'PERMISSION_TENANT_BALANCE_ADD',
  PERMISSION_TENANT_BALANCE_SUBTRACT = 'PERMISSION_TENANT_BALANCE_SUBTRACT',
  PERMISSION_TENANT_TRANSACT_STORNED = 'PERMISSION_TENANT_TRANSACT_STORNED',
  CCB_OPERATION_CREDIT = 'CCB_OPERATION_CREDIT',
  CCB_OPERATION_CANCELLATION = 'CCB_OPERATION_CANCELLATION',
  PERMISSION_SOLUTION_ID_REDACTOR = 'SOLUTION_ID_REDACTOR',
  PERMISSION_LEAD_CONTRACT = 'PERMISSION_LEAD_CONTRACT',
  PERMISSION_WHATSAPP_BUSINESS_VERIFICATION_ACTION = 'PERMISSION_WHATSAPP_BUSINESS_VERIFICATION_ACTION',

  //  Tenant license permissions
  PERMISSION_TENANT_LICENSE_RENEW = 'PERMISSION_TENANT_LICENSE_RENEW',

  // Dialogs
  PERMISSION_DIALOG = 'PERMISSION_DIALOG',
}

enum ERoles {
  ROLE_USER = 'ROLE_USER',
  ROLE_BASIC_USER = 'ROLE_BASIC_USER',
  ROLE_ACCOUNT_MANAGER = 'ROLE_ACCOUNT_MANAGER',
  ROLE_TARIFF_MANAGER = 'ROLE_TARIFF_MANAGER',
  ROLE_SUPPORT = 'ROLE_SUPPORT',
  ROLE_SUPER_USER = 'ROLE_SUPER_USER',
  ROLE_TENANT_LICENSE_RENEW = 'ROLE_TENANT_LICENSE_RENEW',
  ROLE_TENANT_TRANSACT_STORNED = 'ROLE_TENANT_TRANSACT_STORNED',
  ROLE_ENGINEER = 'ROLE_ENGINEER',
  ROLE_PAYKEEPER_CHANGE = 'ROLE_PAYKEEPER_CHANGE',
  ROLE_AGENT = 'ROLE_AGENT',
}

enum EFeatureFlag {
  STRIPE_AUTOPAYMENT = 'EP-3258_stripe_checkout_integration',
  UNTEMPLATE_FLOW_MESSAGE = 'EP-6470_flow_non_template_messages_sending',
  SHOW_HELP_COPYRIGHT = 'EP-7088_show_help_copyright',
  WHATSAPP_PARTNER_VERIFICATION = 'EP-6995_whatsapp_partner_verification',
}

enum EResellerIds {
  OSK_ID = 1,
  MFM_ID = 2,
  PT_ID = 3,
  KZ_ID = 4,
  BLR_FIRST_ID = 5,
  BLR_SECOND_ID = 6,
}

enum EPaymentType {
  POSTPAID = 'POSTPAID',
  PREPAYMENT = 'PREPAYMENT',
  MIXED_PREPAYMENT = 'MIXED_PREPAYMENT',
}

type TPaymentType = keyof typeof EPaymentType;

const PAYMENT_TYPES = Object.values(EPaymentType);

enum EDocumentType {
  CONTRACT = 'CONTRACT', // Бумажный договор
  AGREEMENT = 'AGREEMENT', // Оферта
  TECH_PARTNER_CONTRACT = 'TECH_PARTNER_CONTRACT', // Технический партнер на договоре
  TECH_PARTNER_OFFER = 'TECH_PARTNER_OFFER', // Технический партнер на оферте
  PILOT = 'PILOT', // Пилот
}

const TECH_PARTNER_DOCUMENT_TYPES = [
  EDocumentType.TECH_PARTNER_CONTRACT,
  EDocumentType.TECH_PARTNER_OFFER,
];

type TPaymentData = {
  operBankAccount: string | null;
  corrBankAccount: string | null;
  bankName: string | null;
  taxNumber: string | null;
  kpp: string | null;
  nonresident: boolean;
  postAddress: string | null;
  legalAddress: string | null;
  siteAddress: string | null;
  country: string | null;
  iban: string | null;
  registrationEmail: string | null;
  authorizedPersonName: string | null;
  vatNumber: string | null;
  bic: string | null;
  bankAddress: string | null;
};

type TTenant = {
  resellerId: EResellerIds;
  tenantId: number;
  paymentData: TPaymentData;
  contacts?: {
    email?: string;
    phone?: string;
    country?: string;
  };
  personalManagerId?: number;
  personalManagerName?: string;
  personalManagerEmail?: string;
  personalManagerPhone?: string;
  supportEmail?: string;
  companyName?: string;
  createdAt?: string;
  needAgreementConfirmation: boolean;
  offerContentUrl?: string;
  paymentType?: TPaymentType;
  licensePaymentTerm?: number;
  temporaryBlocked?: boolean;
  unblockAt?: string;
  locked?: boolean;
  defaultCurrency: TCurrency;
  locale?: string;
  timeZone?: string;
  country?: string;
  broadcastMaxMessageSpeed?: number;
  registrationEmail?: string;
  tariffsAcceptedAt?: string;
  selfRegistered?: boolean;
};

type TUserTenant = Partial<
  Pick<
    TTenant,
    'companyName' | 'locked' | 'temporaryBlocked' | 'createdAt' | 'locale' | 'resellerId'
  > &
    Pick<TTenant['paymentData'], 'taxNumber'>
> & {
  id: number;
  currencyCode: TCurrency;
};

type TUser = {
  id: number;
  login: string;
  name: string;
  roles: ERoles[];
  permissions: EPermission[];
  tenants: TUserTenant[];
  timeZone?: string;
  description?: string;
  surveyConsentGranted: boolean;
};

type TBankDetails = {
  id: number;
  resellerId: EResellerIds;
  beneficiaryBank: string;
  ibanIdr: string;
  ibanUsd: string;
  ibanEur: string;
  bankCode: string;
  branchName: string;
  swift: string;
  iban: string;
  accountNumber: string;
  currency: TCurrency;
  paymentType: TPaymentType;
  resellerName: string;
  resellerAddress: string;
  resellerRegNum: string;
  resellerVat: string;
  comment: string;
};

type TTenantUser = {
  id: number;
  login: string;
  name: string;
};

type TRemoveConfig = Record<TItemId, {isRemoved: boolean}>;

type TUserConfig = {
  advices?: TRemoveConfig;
  news?: TRemoveConfig;
  onboardingTutorial?: TRemoveConfig;
};

export {
  EPaymentType,
  PAYMENT_TYPES,
  EDocumentType,
  TECH_PARTNER_DOCUMENT_TYPES,
  EPermission,
  ERoles,
  EResellerIds,
  EFeatureFlag,
};

enum EUserConfigCodes {
  TENANT_WHATSAPP_TARIFF_CHANGE_2025_BANNER_SHOW_DATES = 'TENANT_WHATSAPP_TARIFF_CHANGE_2025_BANNER_SHOW_DATES',
  TENANT_SPECIAL_OFFERS = 'TENANT_SPECIAL_OFFERS',
  TENANT_NOTIFICATION_BALANCE_LOW_LIMITS = 'TENANT_NOTIFICATION_BALANCE_LOW_LIMITS',
  TENANT_IS_BILLING_DISABLED = 'TENANT_IS_BILLING_DISABLED',
}

export type {TTenant, TUserTenant, TPaymentType, TBankDetails, TUser, TTenantUser, TUserConfig};

export {EUserConfigCodes};
